import * as LearningModuleActions from "src/app/store/learning-module/actions";
import * as FromLearningModule from "src/app/store/learning-module/selectors";
import * as FromRouter from "src/app/store/router/selectors";
import { ChangeDetectionStrategy, Component } from "@angular/core";
import { ChapterDto, LearningModuleDto } from "@api";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { Observable } from "rxjs";
import { AppRoutes } from "src/app/constants";
import { RootState } from "src/app/store";
import { first, skipWhile, switchMap } from "rxjs/operators";
import { PopoverButton, PopoverComponent } from "src/app/components/popover/popover.component";
import { AlertController, NavController, PopoverController } from "@ionic/angular";

@Component({
  selector: "app-module-detail",
  templateUrl: "./module-detail.page.html",
  styleUrls: ["./module-detail.page.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModuleDetailPage {
  public readonly routes: typeof AppRoutes = AppRoutes;
  public readonly loading$: Observable<boolean> = this.store.select(FromLearningModule.selectLoading);
  public breadcrumbs: any[] = [];
  public readonly moduleId$: Observable<string> = this.store.select(FromRouter.selectModuleId);
  public readonly learningModule$: Observable<LearningModuleDto> = this.moduleId$.pipe(
    switchMap(moduleId => this.store.select(FromLearningModule.selectLearningModule(moduleId))),
  );
  public readonly chapters$: Observable<ChapterDto[]> = this.moduleId$.pipe(
    switchMap(moduleId => this.store.select(FromLearningModule.selectLearningModuleChapters(moduleId))),
  );

  constructor(
    private readonly store: Store<RootState>,
    private readonly i18n: TranslateService,
    private readonly navController: NavController,
    private readonly alertController: AlertController,
    private readonly popoverController: PopoverController,
  ) {}

  public trackBy(_: number, event: ChapterDto): string {
    return event.id;
  }

  public async presentGeneralPopover(event: Event): Promise<void> {
    const buttons: PopoverButton[] = [
      {
        text: this.i18n.instant("BUTTONS.EDIT"),
        handler: (): void => this.editLearningModule(),
      },
      {
        text: this.i18n.instant("ADMIN_LEARNING_MODULES.ADD_CHAPTER"),
        handler: (): void => this.addChapter(),
      },
      { text: this.i18n.instant("BUTTONS.DELETE"), handler: (): void => this.deleteModule(), danger: true },
    ];
    const popover = await this.popoverController.create({
      component: PopoverComponent,
      componentProps: { buttons },
      event,
    });

    await popover.present();
  }

  public async presentChapterPopover(event: Event, chapter: ChapterDto): Promise<void> {
    event.stopPropagation();
    const buttons: PopoverButton[] = [
      {
        text: this.i18n.instant("BUTTONS.EDIT"),
        handler: (): void => this.editChapter(chapter),
      },
      {
        text: this.i18n.instant("BUTTONS.VIEW"),
        handler: (): void => this.viewChapter(chapter),
      },
      { text: this.i18n.instant("BUTTONS.DELETE"), handler: (): Promise<void> => this.deleteChapter(chapter.id), danger: true },
    ];
    const popover = await this.popoverController.create({
      component: PopoverComponent,
      componentProps: { buttons },
      event,
    });

    await popover.present();
  }

  public viewChapter(chapter: ChapterDto): void {
    this.moduleId$.pipe(first()).subscribe(moduleId => {
      this.navController.navigateForward([AppRoutes.adminModules, moduleId, AppRoutes.chapters, chapter.id]);
    });
  }

  protected ionViewWillEnter(): void {
    this.breadcrumbs = [
      { name: this.i18n.instant("DASHBOARD.BREADCRUMB_NAME"), link: `/${AppRoutes.home}` },
      { name: this.i18n.instant("ADMIN_LEARNING_MODULES.BREADCRUMB_NAME"), link: `/${AppRoutes.adminModules}` },
    ];

    this.learningModule$
      .pipe(
        skipWhile(learningModule => !learningModule),
        first(),
      )
      .subscribe(learningModule => {
        this.breadcrumbs.push({ name: learningModule.name, link: `/${AppRoutes.adminModules}/${learningModule.id}` });
      });
  }

  protected ionViewDidEnter(): void {
    this.moduleId$.pipe(first()).subscribe(learningModuleId => {
      this.store.dispatch(LearningModuleActions.getLearningModule({ learningModuleId }));
      this.store.dispatch(LearningModuleActions.getLearningModuleChapters({ learningModuleId }));
    });
  }

  private editLearningModule(): void {
    this.moduleId$.pipe(first()).subscribe(moduleId => {
      this.navController.navigateForward([AppRoutes.adminModules, moduleId, AppRoutes.edit]);
    });
  }

  private addChapter(): void {
    this.moduleId$.pipe(first()).subscribe(moduleId => {
      this.navController.navigateForward([AppRoutes.adminModules, moduleId, AppRoutes.chapters, AppRoutes.add]);
    });
  }

  private editChapter(chapter: ChapterDto): void {
    this.moduleId$.pipe(first()).subscribe(moduleId => {
      this.navController.navigateForward([AppRoutes.adminModules, moduleId, AppRoutes.chapters, chapter.id, AppRoutes.edit]);
    });
  }

  private deleteModule(): void {
    this.moduleId$.pipe(first()).subscribe(async learningModuleId => {
      const alert = await this.alertController.create({
        header: this.i18n.instant("ADMIN_LEARNING_MODULES.DELETE_MODULE_HEADER"),
        message: this.i18n.instant("ADMIN_LEARNING_MODULES.DELETE_MODULE_MESSAGE"),
        buttons: [
          { role: "cancel", text: this.i18n.instant("BUTTONS.CANCEL") },
          {
            role: "destructive",
            text: this.i18n.instant("BUTTONS.CONFIRM_DELETE"),
            handler: (): void => this.store.dispatch(LearningModuleActions.deleteLearningModule({ learningModuleId })),
          },
        ],
      });

      await alert.present();
    });
  }

  private async deleteChapter(chapterId: string): Promise<void> {
    this.moduleId$.pipe(first()).subscribe(async learningModuleId => {
      const alert = await this.alertController.create({
        header: this.i18n.instant("ADMIN_LEARNING_MODULES.DELETE_CHAPTER_HEADER"),
        message: this.i18n.instant("ADMIN_LEARNING_MODULES.DELETE_CHAPTER_MESSAGE"),
        buttons: [
          { role: "cancel", text: this.i18n.instant("BUTTONS.CANCEL") },
          {
            role: "destructive",
            text: this.i18n.instant("BUTTONS.CONFIRM_DELETE"),
            handler: (): void => this.store.dispatch(LearningModuleActions.deleteChapter({ learningModuleId, chapterId, goBack: false })),
          },
        ],
      });

      await alert.present();
    });
  }
}
